'use client';

import * as React from 'react';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';
import { Input } from '@/hen-ui/atomics/Input';
import { Button } from '@/hen-ui/atomics/Button';
import { Link } from '@/hen-ui/atomics/Link';
import { Flex } from '@/hen-ui/composites/Flex';
import { Grid } from '@/hen-ui/composites/Grid';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';
import type { EmailBannerProps } from './types';

const IDLE_HEADING = 'Stay in the know';
const IDLE_SUBTEXT =
  'Subscribe to our weekly emails and get the latest college news and resources sent straight to your inbox!';
const SUCCESS_HEADING = "You're On the List!";
const SUCCESS_BODY =
  'Get ready for news and expert insights on higher ed. Your first email is already on its way.';

function PrivacyPolicyCopy() {
  return (
    <Text variant='b5' color='subtext' tag='p'>
      Read our{' '}
      <Link href='/privacy-policy' size='sm'>
        Privacy Policy
      </Link>
      .
    </Text>
  );
}

function FormFieldRowSizer() {
  return (
    <Flex
      direction='column'
      direction-md='row'
      gap='xs'
      className='pointer-events-none md:items-start'
      aria-hidden
    >
      <Flex className='h-11 w-full rounded-sm md:flex-1' aria-hidden />
      <Flex
        className='h-11 w-full shrink-0 md:w-auto md:min-w-[7.5rem]'
        aria-hidden
      />
    </Flex>
  );
}

function ErrorMessageSlot({
  errorMessage,
  id,
}: {
  errorMessage?: string;
  id?: string;
}) {
  const hasError = Boolean(errorMessage);

  return (
    <Text
      id={id}
      variant='b5'
      tag='p'
      role={hasError ? 'alert' : undefined}
      className={cn('min-h-[1lh] text-error-500', !hasError && 'invisible')}
    >
      {errorMessage || '\u00a0'}
    </Text>
  );
}

function EmailBannerLayoutSizer() {
  return (
    <Flex
      direction='column'
      gap='md'
      className='pointer-events-none invisible col-start-1 row-start-1'
      aria-hidden
    >
      <Flex direction='column' gap='xs'>
        <Heading variant='h2' tag='h2'>
          {IDLE_HEADING}
        </Heading>
        <Text variant='b4'>{IDLE_SUBTEXT}</Text>
      </Flex>

      <Flex direction='column' gap='xs'>
        <FormFieldRowSizer />
        <ErrorMessageSlot />
        <PrivacyPolicyCopy />
      </Flex>
    </Flex>
  );
}

export function EmailBanner({
  status,
  errorMessage,
  onSubmit,
  className,
}: EmailBannerProps) {
  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const isLoading = status === 'loading';
  const isSuccess = status === 'success';

  const handleSubscribe = () => {
    if (isLoading) {
      return;
    }

    const input = emailInputRef.current;
    if (!input) {
      return;
    }

    if (!input.checkValidity()) {
      input.reportValidity();
      return;
    }

    const email = input.value.trim();
    if (!email) {
      input.reportValidity();
      return;
    }

    onSubmit(email);
  };

  const handleEmailKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    handleSubscribe();
  };

  return (
    <Flex
      data-hen-id={COMPONENT_IDS.EmailBanner}
      data-testid='email-banner'
      direction='column'
      className={cn('rounded-lg bg-neutral-50 p-md md:p-lg', className)}
    >
      <Grid columns={1} className='w-full gap-0'>
        <EmailBannerLayoutSizer />

        <Flex
          direction='column'
          gap={isSuccess ? 'xs' : 'md'}
          justify={isSuccess ? 'center' : 'start'}
          align={isSuccess ? 'center' : 'stretch'}
          height={isSuccess ? 'full' : 'auto'}
          width='full'
          className='col-start-1 row-start-1'
          data-testid='email-banner-content'
        >
          {isSuccess ? (
            <Flex direction='column' gap='xs' align='start' width='full'>
              <Heading variant='h2' tag='h2' align='center'>
                {SUCCESS_HEADING}
              </Heading>
              <Text variant='b4' align='center'>
                {SUCCESS_BODY}
              </Text>
            </Flex>
          ) : (
            <Flex direction='column' gap='md'>
              <Flex direction='column' gap='xs'>
                <Heading variant='h2' tag='h2'>
                  {IDLE_HEADING}
                </Heading>
                <Text variant='b4'>{IDLE_SUBTEXT}</Text>
              </Flex>

              <Flex
                direction='column'
                gap='xs'
                role='form'
                aria-label='Email subscription'
              >
                <Flex
                  direction='column'
                  direction-md='row'
                  gap='xs'
                  className='md:items-start'
                >
                  <Input
                    ref={emailInputRef}
                    id='email-banner-email'
                    type='email'
                    name='email'
                    placeholder='Email address'
                    required
                    disabled={isLoading}
                    error={Boolean(errorMessage)}
                    className='w-full text-body md:flex-1'
                    aria-describedby='email-banner-email-error'
                    aria-label='Email address'
                    onKeyDown={handleEmailKeyDown}
                  />
                  <Button
                    type='button'
                    variant='primaryFill'
                    size='lg'
                    disabled={isLoading}
                    className='w-full shrink-0 md:w-auto'
                    onClick={handleSubscribe}
                  >
                    {isLoading ? 'Subscribing…' : 'Subscribe'}
                  </Button>
                </Flex>
                <ErrorMessageSlot
                  id='email-banner-email-error'
                  errorMessage={errorMessage}
                />
                <PrivacyPolicyCopy />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Grid>
    </Flex>
  );
}

export type { EmailBannerProps, EmailBannerStatus } from './types';
